import React, { useEffect, useRef, useState } from "react";
import { Check, Minus, Star, FileCheck, UserRound } from "lucide-react";
import { useInView } from "./Reveal";

/* The Sift Score, shown rather than mentioned.

   It used to be one bolded phrase in the middle of a paragraph, which asked the
   reader to take on faith the thing the whole marketplace rests on: that the
   person on the other side is knowable before you talk to them.

   The graphic is a breakdown, not a dial. Two reasons. The score is genuinely
   uncapped in `accounts.views._sift_score` — volume of rated contracts and job
   posts accumulate without a ceiling — so a needle pointing at 84/100 would be
   a picture of something that does not exist. And a number on its own is a
   vanity metric; the argument for trusting it is what it is made of.

   It builds itself when it scrolls into view: each line lands, the total counts
   to its new value, and the bar moves. That is the claim acted out — a score is
   accumulated, not assigned. The deduction lands last, after a longer pause,
   and drives the bar backwards. That is the part a competitor's badge cannot
   do: every marketplace score goes up, and this one comes down, hard, at 20
   points per one-star review with no cap.

   Numbers are illustrative of the weights in the code, and the card says so.
   They are not a real member's score. */

/* Credentials are deliberately not a line here, and the card says so
   underneath rather than leaving a gap somebody later fills back in. A
   verified license used to be worth 25 points. It is now worth nothing,
   because every line that remains is something the member did on Sift --
   their profile, their completed work, their ratings -- and a credential
   check is something we went and got from an outside registry about them.
   Scoring people on that and handing the number to employers is not a thing
   we do. See accounts/credential_policy.py. */
const ROWS = [
  { icon: UserRound,   label: "Complete profile",            pts: 20,  why: "photo and headline" },
  { icon: FileCheck,   label: "31 contracts completed here", pts: 62,  why: "on-platform, uncapped" },
  { icon: Star,        label: "4.8 average over 24 ratings", pts: 15,  why: "quality bonus" },
  { icon: Minus,       label: "One 1-star review",           pts: -20, why: "no ceiling on penalties" },
];

/* Running total after each line: 20, 82, 97, 77. The bar is drawn against the
   PEAK rather than the final, so the last step visibly retreats instead of the
   scale quietly rescaling underneath it. */
const TOTALS = ROWS.reduce((acc, r) => [...acc, (acc[acc.length - 1] ?? 0) + r.pts], []);
const FINAL = TOTALS[TOTALS.length - 1];
const PEAK = Math.max(...TOTALS);

/* Paced to be read, not just seen. Each line has to land, be understood, and
   have its effect on the total watched before the next one arrives, so the
   whole build runs about six seconds rather than three.

   It repeats. A visitor who scrolls past mid-cycle, or who arrives back at this
   part of the page, should see the argument rather than a static end state, and
   the deduction is the argument. */
const FIRST_MS = 500;     // beat before the first line lands
const STEP_MS = 950;      // between lines
const DEDUCT_PAUSE = 1100; // extra beat before the deduction, so it reads as a turn
const COUNT_MS = 700;     // how long the total takes to travel
const HOLD_MS = 3200;     // how long the finished card rests before rebuilding

export default function SiftScoreShowcase() {
  const { ref, armed, seen } = useInView();
  const [step, setStep] = useState(ROWS.length);  // lines landed
  const [score, setScore] = useState(FINAL);
  /* True only while the bar is travelling backwards. The deduction should read
     as a movement, not as a verdict: a bar left permanently brick-red is a card
     that says "bad score" forever after, when the number it settles on (77) is
     a good one. */
  const [penalty, setPenalty] = useState(false);
  const timers = useRef([]);
  const raf = useRef(0);

  const reduced = typeof window !== "undefined"
    && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  /* Only rewind to zero once we know this card is armed and waiting offscreen.
     Above the fold, without an observer, or with reduced motion, it renders
     finished — nothing is ever hidden waiting for an animation that will not
     run. */
  useEffect(() => {
    if (armed && !seen && !reduced) { setStep(0); setScore(0); setPenalty(false); }
  }, [armed, seen, reduced]);

  useEffect(() => {
    if (!armed || !seen || reduced) return undefined;

    let stopped = false;

    const countTo = (from, to) => {
      cancelAnimationFrame(raf.current);
      const t0 = performance.now();
      const tick = (t) => {
        const p = Math.min(1, (t - t0) / COUNT_MS);
        const eased = 1 - Math.pow(1 - p, 3);   // easeOutCubic, settles onto the number
        setScore(Math.round(from + (to - from) * eased));
        if (p < 1) raf.current = requestAnimationFrame(tick);
      };
      raf.current = requestAnimationFrame(tick);
    };

    const after = (ms, fn) => { timers.current.push(setTimeout(fn, ms)); };

    const cycle = () => {
      if (stopped) return;
      setStep(0);
      setScore(0);
      setPenalty(false);

      let at = FIRST_MS;
      ROWS.forEach((row, i) => {
        if (row.pts < 0) at += DEDUCT_PAUSE;
        after(at, () => {
          setStep(i + 1);
          countTo(i === 0 ? 0 : TOTALS[i - 1], TOTALS[i]);
          if (row.pts < 0) {
            setPenalty(true);
            // Just past the width transition, so the colour outlasts the
            // movement it is explaining and no longer.
            after(COUNT_MS + 500, () => setPenalty(false));
          }
        });
        at += STEP_MS;
      });

      /* Rebuild, but only for somebody who is actually looking. A loop left
         running in a hidden tab is timers and repaints nobody sees, and it
         would restart mid-cycle the moment the tab came back. */
      after(at + HOLD_MS, () => {
        if (document.visibilityState === "visible") { cycle(); return; }
        const onVisible = () => {
          document.removeEventListener("visibilitychange", onVisible);
          cycle();
        };
        document.addEventListener("visibilitychange", onVisible);
      });
    };

    cycle();

    return () => {
      stopped = true;
      timers.current.forEach(clearTimeout);
      timers.current = [];
      cancelAnimationFrame(raf.current);
    };
  }, [armed, seen, reduced]);

  const landed = step === 0 ? 0 : TOTALS[step - 1];
  const pct = Math.max(2, Math.min(100, (landed / PEAK) * 100));

  return (
    <div ref={ref} className={`sss bl-glass${armed ? " reveal" : ""}${armed && seen ? " is-in" : ""}`}
         role="img"
         aria-label="Illustration of a Sift Score of 77, built from a complete profile, 31 completed contracts and 24 ratings, less 20 points for a one-star review. Credentials are not scored.">
      <style>{CSS}</style>

      <div className="sss__top">
        <div>
          <div className="sss__label">Sift Score</div>
          {/* aria-hidden: the card's own label already states the number, and a
              live region updating every frame is unusable with a screen reader. */}
          <div className="sss__num" aria-hidden="true">{score}</div>
        </div>
        {/* The change that just landed, thrown up beside the total. */}
        <div className="sss__delta" aria-hidden="true">
          {step > 0 && (
            <span key={step} className={ROWS[step - 1].pts < 0 ? "is-down" : undefined}>
              {ROWS[step - 1].pts > 0 ? "+" : "−"}{Math.abs(ROWS[step - 1].pts)}
            </span>
          )}
        </div>
      </div>

      {/* Its own element rather than a border, so it can travel both ways. */}
      <div className="sss__track" aria-hidden="true">
        <div className={`sss__fill${penalty ? " is-down" : ""}`} style={{ width: `${pct}%` }} />
      </div>

      <ul className="sss__rows">
        {ROWS.map(({ icon: Icon, label, pts, why }, i) => (
          <li key={label} className={`${pts < 0 ? "is-down " : ""}${i < step ? "is-in" : ""}`}>
            <span className="sss__ic" aria-hidden="true">
              {pts < 0 ? <Minus size={13} strokeWidth={3} /> : <Check size={13} strokeWidth={3} />}
            </span>
            <span className="sss__txt">
              <span className="sss__lab"><Icon size={13} strokeWidth={2} aria-hidden="true" /> {label}</span>
              <span className="sss__why">{why}</span>
            </span>
            <span className="sss__pts">{pts > 0 ? "+" : "−"}{Math.abs(pts)}</span>
          </li>
        ))}
      </ul>

      <p className="sss__foot">
        Illustrative. Real scores are computed per member and move both ways.
        {" "}Credentials are shown with the date they were checked, and are never scored or ranked.
      </p>
    </div>
  );
}

const CSS = `
.sss { border-radius:var(--r-xl); padding:20px 20px 16px; width:100%; max-width:430px; }

.sss__top { display:flex; align-items:flex-end; justify-content:space-between; gap:16px; }
.sss__label { font-family:var(--font-mono); text-transform:uppercase; font-size:0.65625rem;
  letter-spacing:.16em; color:var(--text-tertiary); }
.sss__num { font-size:3.25rem; line-height:1; font-weight:700; color:var(--brand);
  font-variant-numeric:tabular-nums; letter-spacing:-.03em; margin-top:4px; }

/* Keyed on the step in JSX, so React remounts this and the pop replays for
   every line rather than firing once. */
.sss__delta { height:26px; display:flex; align-items:flex-end; }
.sss__delta span { font-family:var(--font-mono); font-size:0.9375rem; font-weight:600;
  color:var(--brand-ink); font-variant-numeric:tabular-nums; animation:sssPop .5s ease-out both; }
.sss__delta span.is-down { color:#9E3B2F; }
@keyframes sssPop {
  0%   { opacity:0; transform:translateY(7px) scale(.9); }
  35%  { opacity:1; transform:translateY(0) scale(1.07); }
  100% { opacity:1; transform:none; }
}

.sss__track { height:6px; border-radius:999px; background:var(--brand-tint);
  margin-top:14px; overflow:hidden; }
.sss__fill { height:100%; border-radius:999px; background:var(--brand);
  transition:width .55s cubic-bezier(.22,.61,.36,1), background-color .35s; }
.sss__fill.is-down { background:#9E3B2F; }

.sss__rows { list-style:none; margin:18px 0 0; padding:16px 0 0; border-top:1px solid var(--border); }
.sss__rows li { display:flex; align-items:center; gap:11px; padding:7px 0; }
.sss__ic { flex:0 0 auto; width:20px; height:20px; border-radius:999px; display:grid;
  place-items:center; background:var(--brand-tint); color:var(--brand-ink); }
/* A deduction reads as a deduction. Warm brick rather than a signal red, so it
   sits inside the cream palette instead of shouting over it. */
.sss__rows li.is-down .sss__ic { background:#F6E6E2; color:#9E3B2F; }
.sss__rows li.is-down .sss__pts { color:#9E3B2F; }

.sss__txt { min-width:0; display:flex; flex-direction:column; flex:1 1 auto; }
.sss__lab { display:flex; align-items:center; gap:6px; font-size:0.875rem; font-weight:600;
  color:var(--ink); }
.sss__lab svg { color:var(--text-tertiary); flex:0 0 auto; }
.sss__why { font-size:0.75rem; color:var(--text-tertiary); margin-top:1px; padding-left:19px; }

.sss__pts { flex:0 0 auto; font-family:var(--font-mono); font-size:0.875rem; font-weight:600;
  color:var(--brand-ink); font-variant-numeric:tabular-nums; }

.sss__foot { margin:14px 0 0; padding-top:12px; border-top:1px solid var(--border);
  font-size:0.71875rem; color:var(--text-muted); }

/* Lines pop in. The hidden state applies only under .reveal, which this card
   puts on ITSELF and only once JS has confirmed it is offscreen and animating.
   A browser that never runs the animation renders every line in place. */
.sss.reveal .sss__rows li { opacity:0; transform:translateY(9px) scale(.98);
  transition:opacity .34s ease, transform .34s cubic-bezier(.22,.61,.36,1); }
.sss.reveal .sss__rows li.is-in { opacity:1; transform:none; }

@media (prefers-reduced-motion: reduce) {
  .sss.reveal .sss__rows li, .sss.reveal .sss__rows li.is-in {
    opacity:1; transform:none; transition:none; }
  .sss__delta span { animation:none; }
  .sss__fill { transition:none; }
}

@media (max-width:420px) {
  .sss__num { font-size:2.75rem; }
  .sss__why { display:none; }
}
`;
